import { useEffect, useRef, useState } from 'react';
import { Building2, SlidersHorizontal } from 'lucide-react';
import { getCompanies } from '../api/api';
import styles from './CompanyScopePicker.module.scss';

export const NO_COMPANY = '__none__';

// Reusable "Company scope" multi-select filter. Holds the company list and the
// dropdown UI; the selection itself is owned by the parent so it can be shared
// (e.g. between the machine selector and the map, or the workspace switcher).
function CompanyScopePicker({ selectedCompanyIds, setSelectedCompanyIds, className }) {
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handler = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await getCompanies();
        setCompanies(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    };
    fetchCompanies();
  }, []);

  const toggleCompany = companyId => {
    setSelectedCompanyIds(previous =>
      previous.includes(companyId)
        ? previous.filter(id => id !== companyId)
        : [...previous, companyId]
    );
  };

  const companyTags = [...new Set(companies.map(company => company.tag).filter(Boolean))].sort();

  const toggleTagSelection = tag => {
    const tagCompanyIds = companies
      .filter(company => company.tag === tag)
      .map(company => company.company_id);
    setSelectedCompanyIds(previous => {
      const allSelected = tagCompanyIds.every(id => previous.includes(id));
      return allSelected
        ? previous.filter(id => !tagCompanyIds.includes(id))
        : [...new Set([...previous, ...tagCompanyIds])];
    });
  };

  const pickerLabel = () => {
    if (selectedCompanyIds.length === 0) {
      return 'All companies';
    }
    if (selectedCompanyIds.length > 1) {
      return `${selectedCompanyIds.length} companies`;
    }
    const id = selectedCompanyIds[0];
    if (id === NO_COMPANY) {
      return 'No company';
    }
    return companies.find(company => company.company_id === id)?.name || '1 company';
  };

  return (
    <div className={`${styles.companyPickerContainer} ${className || ''}`} ref={containerRef}>
      <button
        type="button"
        className={styles.companyPickerBtn}
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
      >
        <Building2 size={16} />
        <span>{pickerLabel()}</span>
        <SlidersHorizontal className={styles.filterIcon} size={15} />
      </button>

      {open && (
        <div className={styles.companyPickerMenu}>
          <div className={styles.companyPickerActions}>
            <strong>Filter companies</strong>
            <button type="button" onClick={() => setSelectedCompanyIds([])}>
              Clear
            </button>
          </div>
          {companyTags.length > 0 && (
            <div className={styles.tagQuickSelect}>
              {companyTags.map(tag => (
                <button
                  type="button"
                  key={tag}
                  className={styles.tagQuickButton}
                  onClick={() => toggleTagSelection(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
          <div className={styles.companyPickerList}>
            <label className={styles.companyPickerItem}>
              <input
                type="checkbox"
                checked={selectedCompanyIds.includes(NO_COMPANY)}
                onChange={() => toggleCompany(NO_COMPANY)}
              />
              <span className={styles.companyName}>No company</span>
            </label>
            {companies.map(company => (
              <label key={company.company_id} className={styles.companyPickerItem}>
                <input
                  type="checkbox"
                  checked={selectedCompanyIds.includes(company.company_id)}
                  onChange={() => toggleCompany(company.company_id)}
                />
                <span className={styles.companyName}>{company.name}</span>
                {company.tag && <span className={styles.companyTag}>{company.tag}</span>}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyScopePicker;
